import React from "react";

import { Email as EmailIcon, Phone as PhoneIcon } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/client";

interface KeyContactsListProps {
  serviceId: number;
}

export const KeyContactsList: React.FC<KeyContactsListProps> = ({ serviceId }) => {
  // Query for key contacts
  const {
    data: keyContacts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["key-contacts", serviceId],
    queryFn: () => apiClient.service.getKeyContacts(serviceId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !!serviceId,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load key contacts. Please try again.</Alert>;
  }

  if (keyContacts.length === 0) {
    return <Alert severity="info">No key contacts found for this service.</Alert>;
  }

  const getInitials = (name?: string) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatContactType = (contactType?: string) => {
    if (!contactType) return "Contact";
    return contactType.charAt(0).toUpperCase() + contactType.slice(1).toLowerCase();
  };

  const getContactDescription = (position?: string, name?: string) => {
    if (!name) return "";

    const firstName = name.split(" ")[0];

    const contactType = (position || "").toLowerCase();

    if (contactType.includes("key account") || contactType.includes("relationship manager")) {
      return `${firstName}'s role is to support the service by working with stakeholders to ensure all school values are incorporated into the service, organise OSHC information sessions and to create our marketing and promotional plan.`;
    }

    if (contactType.includes("regional manager")) {
      return `${firstName}'s role is to ensure the service and educators meet expectations for an engaging, compliant, quality service and to support the OSHC team at your school.`;
    }

    if (contactType.includes("general manager")) {
      return `${firstName}'s role is to support the Regional Managers in all their endeavours and to constantly ensure the service is meeting all expectations from the school, families, children, and governing bodies. The General Manager is also the escalation point for any concerns or challenges that arise.`;
    }

    if (contactType.includes("head of service delivery")) {
      return `${firstName} works with our General Managers to define our Your OSHC product proposition in a way that is innovative, market leading and differentiated and focuses on delivering service excellence and consistency across our partner schools. This dedicated focus will ensure we continue to meet our compliance objectives and provide a service that delights our children, parents, and principals. If you feel that escalation to the Head of Service Delivery is required, you may contact ${firstName}.`;
    }

    return "";
  };

  return (
    <Grid container spacing={3}>
      {keyContacts.map((contact, index) => {
        const contactDescription = getContactDescription(contact.position, contact.name);
        return (
          <Grid size={{ xs: 12, lg: 8 }} key={index}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "16px",
                border: "2px solid",
                borderColor: "grey.300",
                transition: "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
                "&:hover": {
                  boxShadow: 4,
                  borderColor: "primary.main",
                },
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: "secondary.main", mr: 2 }}>{getInitials(contact.name)}</Avatar>
                  <Box>
                    <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
                      {contact.position || formatContactType(contact.role)}
                    </Typography>
                    <Typography variant="h6" component="div">
                      {contact.name || "Unknown"}
                    </Typography>
                  </Box>
                </Box>

                {contactDescription && (
                  <Box mb={2}>
                    <Typography variant="body1" paragraph>
                      {contactDescription}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {contact.name && (
                  <Box display="flex" mb={1}>
                    <Typography variant="body2" sx={{ fontWeight: "bold", minWidth: "80px" }}>
                      Name:
                    </Typography>
                    <Typography variant="body2">{contact.name}</Typography>
                  </Box>
                )}

                {contact.phone && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="body2" sx={{ fontWeight: "bold", minWidth: "80px" }}>
                      Phone:
                    </Typography>
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {contact.phone}
                    </Typography>
                    <Tooltip title="Call">
                      <IconButton size="small" href={`tel:${contact.phone}`}>
                        <PhoneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {contact.email && (
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: "bold", minWidth: "80px" }}>
                      Email:
                    </Typography>
                    <Typography variant="body2" sx={{ mr: 1, wordBreak: "break-word" }}>
                      {contact.email}
                    </Typography>
                    <Tooltip title="Send email">
                      <IconButton size="small" href={`mailto:${contact.email}`}>
                        <EmailIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};
